import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import JSZip from 'jszip';
import { environment } from '../../environments/environment';
import { endpoints } from '../endpoints';

export interface BuildingLayer {
  id: number;
  name: string;
  parent_layer_id: number;
  feature_count: number;
}

export interface FeatureProperties {
  feature_id: number;
  layer_id: number | null;
  level_id: number | null;
  style_id: number | null;
  geo_type: string | null;
  heading: number | null;
  streetview_id?: number;
  label?: string | null;
  file_name?: string | null;
  is_entrance?: boolean;
  is_path_blocked?: boolean;
  sv_heading?: number | null;
  links?: Array<{ dest_id: number; heading: number | null }>;
}

export interface BuildingFeaturesResponse {
  layers: BuildingLayer[];
  building_center: { lng: number; lat: number } | null;
  truncated: boolean;
  max_features: number;
  features: {
    type: 'FeatureCollection';
    features: Array<{
      type: 'Feature';
      geometry: any; // Point | LineString | Polygon | MultiLineString | MultiPolygon
      properties: FeatureProperties;
    }>;
  };
}

export interface BulkBatchedRequest {
  buildingId: number;
  levelId: number;
  zipFile: File;
  geojsonFile: File;
  logoFile?: File | null;
  replaceExisting?: boolean;
  // Photos per HTTP request. Default 10; set lower if your upstream cap
  // sits below ~10 × 20MB ≈ 200MB.
  jpgsPerBatch?: number;
}

export type BulkBatchProgress =
  // Browser has finished JSZip-extracting the source zip.
  | { type: 'split'; totalBatches: number; totalJpgs: number }
  // About to POST a batch.
  | { type: 'batch_start'; batchIndex: number; totalBatches: number; jpgCount: number }
  // Bytes-uploaded ticks from the current XHR; `overallPercent` factors in
  // both the completed batches and the in-flight one.
  | { type: 'progress'; batchIndex: number; totalBatches: number; batchPercent: number; overallPercent: number }
  // A batch's response landed (success or non-2xx — `_runBatched` will fire
  // an `error` after this if the status was bad).
  | { type: 'batch_done'; batchIndex: number; totalBatches: number; batchResult: any }
  // Every batch succeeded. `aggregated` is the merged summary across all batches.
  | { type: 'all_done'; aggregated: AggregatedBulkResult }
  // `httpStatus` lets the UI distinguish a "level is locked by another
  // admin" 409 from a generic failure so it can render a clearer banner.
  | { type: 'error'; message: string; batchIndex?: number; httpStatus?: number };

export interface AggregatedBulkResult {
  buildingId: number;
  levelId: number;
  layerId: number | null;
  batchCount: number;
  processedCount: number;
  matchedCount: number;
  unmatchedJpgCount: number;
  unmatchedJpgs: Array<{ fileName: string; pointId: number | string | null }>;
  skippedJpgCount: number;
  skippedJpgs: Array<{
    fileName: string;
    pointId: number | string | null;
    reason: string;
    existingStreetviewId?: number;
  }>;
  unmatchedPointIds: Array<number | string>;
  unindexableGeojsonFeatureCount: number;
  purged: {
    featuresDeleted: number;
    streetviewsDeleted: number;
    streetviewLinksDeleted: number;
    blobsDeleted: number;
  };
  results: any[];
}

export class BulkBatchController {
  private aborted = false;
  private currentXhr: XMLHttpRequest | null = null;

  setXhr(xhr: XMLHttpRequest | null): void {
    this.currentXhr = xhr;
  }
  abort(): void {
    this.aborted = true;
    this.currentXhr?.abort();
    this.currentXhr = null;
  }
  isAborted(): boolean {
    return this.aborted;
  }
}

@Injectable({ providedIn: 'root' })
export class StreetviewService {
  constructor(private http: HttpClient) {}

  getFeaturesForBuilding(
    buildingId: number,
    levelId: number,
    opts?: {
      layerIds?: number[];
      // "swLng,swLat,neLng,neLat" — viewport-scoped fetch via MBRIntersects
      bbox?: string;
      // Tolerance in degrees for ST_Simplify on geometries. Match it to the
      // user's current zoom: ~0.000001 at street zoom, ~0.01 at country zoom.
      simplify?: number;
    }
  ): Observable<BuildingFeaturesResponse> {
    const qs: string[] = [`level_id=${encodeURIComponent(String(levelId))}`];
    if (opts?.layerIds && opts.layerIds.length > 0) {
      qs.push(`layer_ids=${encodeURIComponent(opts.layerIds.join(','))}`);
    }
    if (opts?.bbox) {
      qs.push(`bbox=${encodeURIComponent(opts.bbox)}`);
    }
    if (opts?.simplify && opts.simplify > 0) {
      qs.push(`simplify=${encodeURIComponent(String(opts.simplify))}`);
    }
    const url =
      environment.apiBaseUrl +
      endpoints.getFeaturesForBuilding.replace(':buildingId', String(buildingId)) +
      '?' + qs.join('&');
    return this.http.get<BuildingFeaturesResponse>(url, { withCredentials: true });
  }

  /**
   * Splits the user's source zip into batches of N JPGs in the browser, then
   * POSTs each batch sequentially. Returns a controller whose `.abort()`
   * cancels both the in-flight XHR and any remaining batches.
   *
   * Events fired in order:
   *   split → (batch_start → progress* → batch_done) × N → all_done | error
   */
  uploadBulkBatched(
    req: BulkBatchedRequest,
    onEvent: (e: BulkBatchProgress) => void
  ): BulkBatchController {
    const ctrl = new BulkBatchController();
    void this.runBatched(req, onEvent, ctrl).catch(err => {
      if (ctrl.isAborted()) return;
      onEvent({ type: 'error', message: err?.message || String(err) });
    });
    return ctrl;
  }

  private async runBatched(
    req: BulkBatchedRequest,
    onEvent: (e: BulkBatchProgress) => void,
    ctrl: BulkBatchController
  ): Promise<void> {
    const jpgsPerBatch = Math.max(1, req.jpgsPerBatch ?? 10);

    // Read the source zip in-memory. JSZip handles large archives via streamed
    // decompression; we don't pull every entry's bytes here, just the index.
    const sourceZip = await JSZip.loadAsync(req.zipFile);

    // Mirror the backend's `_extract_zip_safely` + JPG filter: only root-level
    // .jpg entries. Anything in a subdir or with another extension is dropped.
    const jpgEntries: { name: string; entry: JSZip.JSZipObject }[] = [];
    sourceZip.forEach((relativePath, entry) => {
      if (entry.dir) return;
      if (relativePath.includes('/')) return;
      if (!relativePath.toLowerCase().endsWith('.jpg')) return;
      jpgEntries.push({ name: relativePath, entry });
    });

    if (jpgEntries.length === 0) {
      onEvent({
        type: 'error',
        message: 'ZIP file does not contain any JPG files in its root directory.'
      });
      return;
    }

    // Sort by name for deterministic batch composition — makes a re-run with
    // the same source produce the same per-batch contents (handy for diffing
    // failed-batch logs).
    jpgEntries.sort((a, b) => a.name.localeCompare(b.name));

    const totalBatches = Math.ceil(jpgEntries.length / jpgsPerBatch);
    onEvent({ type: 'split', totalBatches, totalJpgs: jpgEntries.length });

    const aggregated = this.emptyAggregated(req);

    for (let batchIdx = 0; batchIdx < totalBatches; batchIdx++) {
      if (ctrl.isAborted()) return;

      const slice = jpgEntries.slice(batchIdx * jpgsPerBatch, (batchIdx + 1) * jpgsPerBatch);

      // Build the mini-zip. STORE (no compression) — JPGs barely shrink under
      // DEFLATE and skipping it cuts CPU time noticeably on big batches.
      const mini = new JSZip();
      for (const { name, entry } of slice) {
        if (ctrl.isAborted()) return;
        const buf = await entry.async('uint8array');
        mini.file(name, buf);
      }
      const miniBlob = await mini.generateAsync({
        type: 'blob',
        compression: 'STORE',
        mimeType: 'application/zip'
      });
      const miniFile = new File([miniBlob], `batch-${batchIdx + 1}.zip`, {
        type: 'application/zip'
      });

      onEvent({
        type: 'batch_start',
        batchIndex: batchIdx + 1,
        totalBatches,
        jpgCount: slice.length
      });

      // Replace-existing fires only on batch 1 — otherwise batch 2 would purge
      // batch 1's commits. Subsequent batches run in append mode; the backend's
      // content-hash dedup + `_existing_coverage_point_ids` keep the
      // per-batch warnings sane.
      const isFirst = batchIdx === 0;

      let response: { status: number; body: any };
      try {
        response = await this.uploadOneBatch(
          {
            buildingId: req.buildingId,
            levelId: req.levelId,
            zipFile: miniFile,
            geojsonFile: req.geojsonFile,
            logoFile: req.logoFile ?? null,
            replaceExisting: isFirst && !!req.replaceExisting
          },
          pct => {
            const overall = ((batchIdx + pct / 100) / totalBatches) * 100;
            onEvent({
              type: 'progress',
              batchIndex: batchIdx + 1,
              totalBatches,
              batchPercent: pct,
              overallPercent: overall
            });
          },
          ctrl
        );
      } catch (err: any) {
        if (ctrl.isAborted()) return;
        onEvent({
          type: 'error',
          batchIndex: batchIdx + 1,
          message: `Batch ${batchIdx + 1}/${totalBatches} failed: ${err?.message || err}`
        });
        return;
      }

      if (ctrl.isAborted()) return;

      onEvent({
        type: 'batch_done',
        batchIndex: batchIdx + 1,
        totalBatches,
        batchResult: response.body
      });

      if (response.status < 200 || response.status >= 300) {
        const env = response.body || {};
        const serverMsg = env.description || env.message || `HTTP ${response.status}`;
        // 409 = another upload is currently holding the (building, level)
        // advisory lock. Surface the server's message verbatim — it tells
        // the user to "try again in a moment" — and prefix with the batch
        // index so they know how far the sequence got.
        const isConflict = response.status === 409;
        const prefix = isConflict
          ? `Couldn't continue at batch ${batchIdx + 1}/${totalBatches}`
          : `Batch ${batchIdx + 1}/${totalBatches} failed`;
        onEvent({
          type: 'error',
          batchIndex: batchIdx + 1,
          httpStatus: response.status,
          message: `${prefix}: ${serverMsg}`
        });
        return;
      }

      this.mergeBatch(aggregated, response.body?.data);
    }

    this.finalizeAggregated(aggregated);
    onEvent({ type: 'all_done', aggregated });
  }

  /** Single-batch XHR. Resolves with `{status, body}` for any HTTP response;
   *  rejects only on network/abort errors. Surfaces upload bytes via
   *  `onProgress` (0..100). */
  private uploadOneBatch(
    req: {
      buildingId: number;
      levelId: number;
      zipFile: File;
      geojsonFile: File;
      logoFile: File | null;
      replaceExisting: boolean;
    },
    onProgress: (pct: number) => void,
    ctrl: BulkBatchController
  ): Promise<{ status: number; body: any }> {
    return new Promise((resolve, reject) => {
      if (ctrl.isAborted()) {
        reject(new Error('cancelled'));
        return;
      }

      const url =
        environment.apiBaseUrl +
        endpoints.bulkUploadStreetviews.replace(
          ':buildingId',
          encodeURIComponent(String(req.buildingId))
        );

      const xhr = new XMLHttpRequest();
      xhr.open('POST', url, true);
      xhr.withCredentials = true;

      const fd = new FormData();
      fd.append('level_id', String(req.levelId));
      fd.append('zip_file', req.zipFile, req.zipFile.name);
      fd.append('geojson_file', req.geojsonFile, req.geojsonFile.name);
      if (req.logoFile) fd.append('logo_file', req.logoFile, req.logoFile.name);
      if (req.replaceExisting) fd.append('replace_existing', 'true');

      // Map upload bytes 0..100 → 0..80 of the batch; the remaining 20 covers
      // server-side processing (image pipeline + GCS upload). Bumps to 100
      // when the response lands so the bar visibly clears between batches.
      xhr.upload.addEventListener('progress', ev => {
        if (ev.lengthComputable) {
          onProgress((ev.loaded / ev.total) * 80);
        }
      });

      xhr.onload = () => {
        let body: any = {};
        try {
          body = JSON.parse(xhr.responseText || '{}');
        } catch {
          body = {};
        }
        onProgress(100);
        ctrl.setXhr(null);
        resolve({ status: xhr.status, body });
      };

      xhr.onerror = () => {
        ctrl.setXhr(null);
        reject(new Error('Network error during upload.'));
      };
      xhr.onabort = () => {
        ctrl.setXhr(null);
        reject(new Error('Upload cancelled.'));
      };

      ctrl.setXhr(xhr);
      xhr.send(fd);
    });
  }

  private emptyAggregated(req: BulkBatchedRequest): AggregatedBulkResult {
    return {
      buildingId: req.buildingId,
      levelId: req.levelId,
      layerId: null,
      batchCount: 0,
      processedCount: 0,
      matchedCount: 0,
      unmatchedJpgCount: 0,
      unmatchedJpgs: [],
      skippedJpgCount: 0,
      skippedJpgs: [],
      unmatchedPointIds: [],
      unindexableGeojsonFeatureCount: 0,
      purged: {
        featuresDeleted: 0,
        streetviewsDeleted: 0,
        streetviewLinksDeleted: 0,
        blobsDeleted: 0
      },
      results: []
    };
  }

  private mergeBatch(agg: AggregatedBulkResult, data: any): void {
    if (!data) return;
    agg.batchCount += 1;
    if (typeof data.layerId === 'number') agg.layerId = data.layerId;
    agg.processedCount += data.processedCount || 0;
    agg.matchedCount += data.matchedCount || 0;
    agg.unmatchedJpgCount += data.unmatchedJpgCount || 0;
    if (Array.isArray(data.unmatchedJpgs)) agg.unmatchedJpgs.push(...data.unmatchedJpgs);
    agg.skippedJpgCount += data.skippedJpgCount || 0;
    if (Array.isArray(data.skippedJpgs)) agg.skippedJpgs.push(...data.skippedJpgs);
    if (Array.isArray(data.results)) agg.results.push(...data.results);

    // The backend's `_existing_coverage_point_ids` excludes any point already
    // covered by an earlier-committed batch, so the LAST batch's value is the
    // true "no photo for this point" set. Earlier batches' lists are stale.
    if (Array.isArray(data.unmatchedPointIds)) {
      agg.unmatchedPointIds = data.unmatchedPointIds.slice();
    }

    // Deterministic from the GeoJSON file — same on every batch. Take the
    // latest so a transient zero from an empty-features early batch doesn't
    // mask a real value.
    if (typeof data.unindexableGeojsonFeatureCount === 'number') {
      agg.unindexableGeojsonFeatureCount = data.unindexableGeojsonFeatureCount;
    }

    // Purge counts only land on batch 1 (the only one we send replace_existing
    // for). max() so a later batch with `purged: {0,0,0,0}` can't clobber it.
    if (data.purged) {
      agg.purged.featuresDeleted = Math.max(
        agg.purged.featuresDeleted,
        data.purged.featuresDeleted || 0
      );
      agg.purged.streetviewsDeleted = Math.max(
        agg.purged.streetviewsDeleted,
        data.purged.streetviewsDeleted || 0
      );
      agg.purged.streetviewLinksDeleted = Math.max(
        agg.purged.streetviewLinksDeleted,
        data.purged.streetviewLinksDeleted || 0
      );
      agg.purged.blobsDeleted = Math.max(
        agg.purged.blobsDeleted,
        data.purged.blobsDeleted || 0
      );
    }
  }

  private finalizeAggregated(agg: AggregatedBulkResult): void {
    agg.results.sort((a, b) =>
      String(a.streetviewId || '').localeCompare(String(b.streetviewId || ''))
    );
  }
}
